import { Plus } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { FloatingButton } from '@unistocks/components/button/floating-action-button';
import {
  Gridster,
  GridsterItem,
  GridsterItemConfig,
  GridsterOptions
} from '@unistocks/components/gridster';
import { DashboardItemLayout } from '@unistocks/features/dashboard/components/dashboard-item/dashboard-item';
import { DashboardGridProps } from '@unistocks/features/dashboard/dashboard-grid-props';
import { DashboardList } from '@unistocks/features/dashboard/dashboard-list';
import { computeLayout } from '@unistocks/features/dashboard/grid-utils';
import { getDashboardItemDescriptor } from '@unistocks/features/dashboard/widget-registry';
import { ApplicationConfigurationDto, DashboardItemDto } from '@unistocks/proxy/axios-client';
import {
  useDashboardGetQuery
} from '@unistocks/proxy/axios-client/DashboardQuery';
import {
  useProfileUpdateSettingsMutation
} from '@unistocks/proxy/axios-client/ProfileQuery';
import { APP_CONFIG_QUERY_KEY } from '@unistocks/shared/hooks/use-application-configuration';
import { useSettingValue } from '@unistocks/shared/hooks/use-setting-value';
import { UnistocksSettings } from '@unistocks/shared/settings';
import { useMemo } from 'react';
import { getVariableValue, useMedia, View } from 'tamagui';

export const DashboardGrid: React.FC<DashboardGridProps> = ({
  settingsOpen,
  openSettings,
  addItemOpen,
  openAddItem
}) => {
  const queryClient = useQueryClient();
  const media = useMedia();
  const colWidth = getVariableValue('$3', 'size');
  const rowHeight = getVariableValue('$3', 'size');

  const { data: dashboard = [] } = useDashboardGetQuery({
    select: data => data?.items ?? []
  });

  const savedLayout = useSettingValue<string | null>(UnistocksSettings.DashboardLayout, null);
  const layoutById = useMemo(
    () => new Map(computeLayout(savedLayout ?? null, dashboard).map(entry => [entry.id, entry])),
    [dashboard, savedLayout]
  );

  const gridsterOptions: GridsterOptions = {
    fixedColWidth: colWidth,
    fixedRowHeight: rowHeight,
    margin: 15,
    outerMarginTop: 15,
    outerMarginRight: 15,
    outerMarginBottom: 15,
    outerMarginLeft: 15,
    draggable: {
      enabled: true,
      delayStart: 200,
      dragHandleClass: 'drag-handle',
      ignoreContentClass: 'prevent-drag'
    },
    resizable: {
      enabled: true,
      handles: { nw: true, se: true, sw: true }
    },
    pushItems: true,
    swap: true,
    swapWhileDragging: true
  };

  const { mutate: updateSettings } = useProfileUpdateSettingsMutation({
    onMutate: params => {
      void queryClient.cancelQueries({ queryKey: APP_CONFIG_QUERY_KEY });

      queryClient.setQueryData(APP_CONFIG_QUERY_KEY, (appConfig: ApplicationConfigurationDto) => {
        return {
          ...appConfig,
          setting: {
            ...appConfig.setting,
            values: {
              ...appConfig.setting.values,
              [UnistocksSettings.DashboardLayout]:
                params.settings[UnistocksSettings.DashboardLayout]
            }
          }
        } as ApplicationConfigurationDto;
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: APP_CONFIG_QUERY_KEY });
    }
  });

  const handleItemChange = (items: GridsterItemConfig[]) => {
    const nextLayout: Partial<GridsterItemConfig>[] = items.map(({ id, x, y, cols, rows }) => ({
      id,
      x,
      y,
      cols,
      rows
    }));

    updateSettings({
      settings: {
        [UnistocksSettings.DashboardLayout]: JSON.stringify(nextLayout)
      }
    });
  };

  const renderDashboardItem = (
    item: DashboardItemDto,
    itemLayout: DashboardItemLayout,
    fillHeight = false
  ) => {
    const DashboardItem = getDashboardItemDescriptor(item.type).component;

    return (
      <DashboardItem
        item={item}
        itemLayout={itemLayout}
        openSettings={openSettings}
        h={fillHeight ? '100%' : undefined}
      />
    );
  };

  if (!media.md) {
    return (
      <DashboardList
        settingsOpen={settingsOpen}
        openSettings={openSettings}
        addItemOpen={addItemOpen}
        openAddItem={openAddItem}
      />
    );
  }

  return (
    <View flex={1} $md={{ ml: '$-5', mr: '$-5' }}>
      <Gridster options={gridsterOptions} onItemChange={handleItemChange}>
        {dashboard.map(item => (
          <GridsterItem key={item.id} item={layoutById.get(item.id)!}>
            {size =>
              renderDashboardItem(
                item,
                {
                  x: size.x,
                  y: size.y,
                  width: size.width,
                  height: size.height
                } as DashboardItemLayout,
                true
              )
            }
          </GridsterItem>
        ))}
      </Gridster>
      <FloatingButton Icon={Plus} open={addItemOpen} onPress={openAddItem} />
    </View>
  );
};

DashboardGrid.displayName = 'DashboardGrid';
